import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MemberLayout } from "../../components/Layouts";
import { api, readUser, fmt, errMsg, imgSrc } from "../../lib/api";
import { Copy, QrCode, Download, Check, Clock, ArrowRight, ChevronDown, Image as ImageIcon } from "lucide-react";

const qrSrc = (text) => text ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(text)}` : "";

function formatCountdown(reservedUntil, nowMs) {
  if (!reservedUntil) return null;
  const ts = Date.parse(reservedUntil);
  if (Number.isNaN(ts)) return null;
  const diff = ts - nowMs;
  if (diff <= 0) return { expired: true, label: "Expired" };
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return { expired: false, label: `Expires in ${h}h ${m}m` };
}

async function downloadQrPng(wallet, amount, reference) {
  if (!wallet) return;
  try {
    const res = await fetch(qrSrc(wallet));
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `helpingagent-deposit-${reference || "qr"}-${amount || "usdt"}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
  } catch (_) {
    window.open(qrSrc(wallet), "_blank");
  }
}

export default function Deposits() {
  const user = readUser();
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [settings, setSettings] = useState({});
  const [rows, setRows] = useState([]);
  const [notice, setNotice] = useState({ ok: false, msg: "" });
  const [copiedKey, setCopiedKey] = useState("");
  const [nowMs, setNowMs] = useState(Date.now());
  const [openId, setOpenId] = useState(null); // package id currently expanded
  const [tickets, setTickets] = useState({}); // { [pkgId]: { expected_amount, reserved_until, awaiting } }
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  function copyText(text, key) {
    if (!text && text !== 0) return;
    try {
      navigator.clipboard?.writeText(String(text));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? "" : k)), 1500);
    } catch (_) {}
  }

  async function load() {
    const [p, s, d] = await Promise.all([
      api.get("/packages"),
      api.get("/settings/public"),
      api.get("/deposits"),
    ]);
    setPackages(p.data);
    setSettings(s.data || {});
    setRows(d.data || []);
    // Auto-open from ?package= query (e.g. after /packages → Activate)
    const pid = params.get("package");
    if (pid) {
      const pkg = p.data.find((x) => x.id === pid);
      if (pkg) await togglePackage(pkg, s.data || {}, true);
    }
  }
  useEffect(() => { load().catch(() => {}); /* eslint-disable-line */ }, []);

  async function togglePackage(pkg, s = settings, forceOpen = false) {
    // Collapse if clicking the already-open one
    if (!forceOpen && openId === pkg.id) {
      setOpenId(null);
      return;
    }
    setNotice({ ok: false, msg: "" });
    if (!s.deposit_wallet) {
      setNotice({ ok: false, msg: "Platform is still in SETUP mode — the deposit wallet has not been published yet. Please try again shortly." });
      setOpenId(pkg.id);
      return;
    }
    // Reuse existing ticket when available so re-opening is instant
    if (tickets[pkg.id]) {
      setOpenId(pkg.id);
      return;
    }
    setLoadingId(pkg.id);
    try {
      // Idempotent reservation: backend returns the same awaiting-match row
      // for this user+package so flipping between tiers never leaks duplicates.
      const r = await api.post("/deposits", { package_id: pkg.id, amount: pkg.price, transaction_hash: "" });
      setTickets((prev) => ({
        ...prev,
        [pkg.id]: {
          expected_amount: r.data?.expected_amount ?? pkg.price,
          reserved_until: r.data?.reserved_until,
          awaiting: !!r.data?.awaiting_match,
        },
      }));
      setOpenId(pkg.id);
      setNotice({ ok: true, msg: `Send EXACTLY $${Number(r.data?.expected_amount ?? pkg.price).toFixed(2)} USDT to the deposit address — we auto-match the moment it lands.` });
      const fresh = await api.get("/deposits");
      setRows(fresh.data || []);
    } catch (e) {
      setNotice({ ok: false, msg: errMsg(e) });
    } finally {
      setLoadingId(null);
    }
  }

  const wallet = settings.deposit_wallet || "";
  const mode = settings.mode || "SETUP";
  const required = Number(settings.required_confirmations || 12);

  // Rows waiting to be matched — show as "watching" list.
  const openRows = useMemo(() => rows.filter((r) => r.awaiting_match || r.status === "Pending" || r.status === "Verified"), [rows]);
  const historyRows = useMemo(() => rows.filter((r) => !openRows.includes(r)), [rows, openRows]);

  return (
    <MemberLayout user={user} active="Packages">
      <div className="pkg-shop-head">
        <div>
          <span className="eyebrow">AVAILABLE PACKAGES</span>
          <h2>Pick a package<br /><em>and get your deposit address.</em></h2>
        </div>
        <p>Each package carries a unique daily production and term length. Tap one and we'll reserve a one-of-a-kind cent-level amount for you — send exactly that to the deposit wallet and your package activates automatically.</p>
      </div>

      {mode !== "LIVE" && (
        <div className="warn">The platform is in <strong>SETUP</strong> mode. You can preview packages, but deposits cannot be processed until the operations team flips the switch.</div>
      )}
      {notice.msg && <div className={notice.ok ? "success" : "error"} data-testid="pkg-shop-notice">{notice.msg}</div>}

      <section className="pkg-shop-grid" data-testid="pkg-shop-grid">
        {packages.map((p, i) => {
          const isOpen = openId === p.id;
          const ticket = tickets[p.id];
          const countdown = ticket?.reserved_until ? formatCountdown(ticket.reserved_until, nowMs) : null;
          return (
            <div key={p.id} className={`pkg-shop-cell ${isOpen ? "is-open" : ""}`}>
              <article className={`pkg-shop-card ${isOpen ? "is-selected" : ""}`} data-testid={`pkg-shop-card-${i}`}>
                {p.image_url ? (
                  <div className="pkg-shop-image" style={{ backgroundImage: `url(${imgSrc(p.image_url)})` }}>
                    <span className="pkg-shop-badge">0{i + 1}</span>
                  </div>
                ) : (
                  <div className="pkg-shop-image pkg-shop-image-empty">
                    <span className="pkg-shop-badge">0{i + 1}</span>
                    <ImageIcon size={28} />
                  </div>
                )}
                <div className="pkg-shop-body">
                  <h3>{p.name}</h3>
                  <p className="pkg-shop-desc">{p.description || "A transparent participation tier with daily production credited to your balance."}</p>
                  <div className="pkg-shop-price">${Number(p.price).toLocaleString()}<small> / package</small></div>
                  <ul className="pkg-shop-stats">
                    <li><span>Duration</span><b>{p.days} days</b></li>
                    <li><span>Daily production</span><b>${p.daily_production}</b></li>
                    <li><span>End of term</span><b>${p.end_of_term}</b></li>
                    <li><span>Referral levels</span><b>4 tiers</b></li>
                  </ul>
                  <button
                    type="button"
                    className={`btn btn-accent full pkg-shop-toggle ${isOpen ? "is-open" : ""}`}
                    data-testid={`pkg-shop-deposit-${i}`}
                    aria-expanded={isOpen}
                    aria-controls={`pkg-accordion-${p.id}`}
                    disabled={loadingId === p.id}
                    onClick={() => togglePackage(p)}
                  >
                    {loadingId === p.id
                      ? "Reserving…"
                      : isOpen
                        ? <>Hide deposit details <ChevronDown size={15} className="pkg-chevron" /></>
                        : <>Deposit ${Number(p.price).toLocaleString()} <ArrowRight size={15} /></>}
                  </button>
                </div>
              </article>

              {/* Accordion — opens directly under the clicked card */}
              <div
                id={`pkg-accordion-${p.id}`}
                className={`pkg-accordion ${isOpen ? "open" : ""}`}
                data-testid={isOpen ? "pkg-accordion-open" : `pkg-accordion-${i}`}
                aria-hidden={!isOpen}
              >
                <div className="pkg-accordion-inner">
                  {ticket ? (
                    <>
                      <div className="pkg-accordion-head">
                        <span className="eyebrow"><span className="pulse" /> YOUR DEPOSIT TICKET · {p.name}</span>
                        <h4>Send EXACTLY <strong>${Number(ticket.expected_amount).toFixed(2)}</strong> USDT (BEP20)</h4>
                      </div>
                      <div className="pkg-accordion-grid">
                        <div className="pkg-accordion-left">
                          <div className="pkg-deposit-row">
                            <span>Deposit wallet (BEP20 · BNB Smart Chain)</span>
                            <div className="pkg-deposit-wallet">
                              <code data-testid={`pkg-deposit-wallet-${i}`}>{wallet || "—"}</code>
                              {wallet && (
                                <button className="btn btn-ghost" onClick={() => copyText(wallet, `wallet-${i}`)} data-testid={`pkg-copy-wallet-${i}`}>
                                  {copiedKey === `wallet-${i}` ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="pkg-deposit-row">
                            <span>Amount to send</span>
                            <div className="pkg-deposit-amount">
                              <strong data-testid={`pkg-deposit-amount-${i}`}>${Number(ticket.expected_amount).toFixed(2)}</strong>
                              <button className="btn btn-accent" onClick={() => copyText(Number(ticket.expected_amount).toFixed(2), `amount-${i}`)} data-testid={`pkg-copy-amount-${i}`}>
                                {copiedKey === `amount-${i}` ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                              </button>
                            </div>
                          </div>
                          <ul className="pkg-deposit-meta">
                            <li><span>Required confirmations</span><b>{required}</b></li>
                            <li><span>Status</span><b>{ticket.awaiting ? "Watching chain" : "Reserved"}</b></li>
                            {countdown && <li><span>Reservation</span><b className={countdown.expired ? "expired" : ""}><Clock size={12} /> {countdown.label}</b></li>}
                          </ul>
                          <p className="note">A unique cent suffix (e.g. $3.12, $10.47) is reserved for you — <strong>send that exact amount</strong>. The monitor matches each on-chain transfer to the member who holds the matching amount, so money is never attributed to the wrong account.</p>
                        </div>
                        <div className="pkg-accordion-right">
                          {wallet ? (
                            <>
                              <div className="qr-wrap"><img alt="Deposit QR" src={qrSrc(wallet)} data-testid={`pkg-deposit-qr-${i}`} /></div>
                              <button className="btn btn-ghost" onClick={() => downloadQrPng(wallet, Number(ticket.expected_amount).toFixed(2), user?.deposit_reference)} data-testid={`pkg-deposit-qr-download-${i}`}><QrCode size={14} /> <Download size={14} /> Download QR</button>
                            </>
                          ) : (
                            <div className="qr-placeholder">Wallet pending</div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="pkg-accordion-empty"><p>Preparing your unique deposit amount…</p></div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {!packages.length && <div className="empty-activity"><p>No packages are published yet.</p></div>}
      </section>

      <section className="panel" style={{ marginTop: 28 }}>
        <div className="panel-heading"><div><span className="eyebrow">OPEN TICKETS</span><h3>Awaiting on-chain match</h3></div></div>
        {openRows.length ? (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Package</th><th>Send amount</th><th>Status</th><th>Reserved until</th></tr></thead>
              <tbody>
                {openRows.map((d, i) => {
                  const amt = d.expected_amount ?? d.amount;
                  const c = d.reserved_until ? formatCountdown(d.reserved_until, nowMs) : null;
                  return (
                    <tr key={d.transaction_hash} data-testid={`open-row-${i}`}>
                      <td>{d.package_name}</td>
                      <td>
                        <strong>${Number(amt).toFixed(2)}</strong>
                        <button className="icon-btn amount-copy-mini" onClick={() => copyText(Number(amt).toFixed(2), `open-${i}`)} data-testid={`copy-open-amount-${i}`}>
                          {copiedKey === `open-${i}` ? <Check size={13} /> : <Copy size={13} />}
                        </button>
                      </td>
                      <td><span className={`status ${d.status.toLowerCase().replace(/\s/g, "-")}`}>{d.status}</span></td>
                      <td>{c ? <small className={`countdown ${c.expired ? "expired" : ""}`}><Clock size={10} /> {c.label}</small> : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-activity"><p>No open tickets. Pick a package above to create one.</p></div>
        )}
      </section>

      {historyRows.length > 0 && (
        <section className="panel" style={{ marginTop: 20 }}>
          <div className="panel-heading"><div><span className="eyebrow">HISTORY</span><h3>Past deposits</h3></div></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Package</th><th>Base</th><th>TX</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {historyRows.map((d, i) => (
                  <tr key={d.transaction_hash}>
                    <td>{d.package_name}</td>
                    <td>${fmt(d.amount)}</td>
                    <td className="hash">{(d.transaction_hash || "").slice(0, 16)}…</td>
                    <td><span className={`status ${d.status.toLowerCase().replace(/\s/g, "-")}`}>{d.status}</span></td>
                    <td>{new Date(d.created_at).toLocaleString("en-US")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </MemberLayout>
  );
}
